// @ts-nocheck
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  ArrowLeft,
  Play,
  Trash2,
  Upload,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Hash,
  ExternalLink,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { DataTable } from '../../components/DataTable';
import { DeploymentCSVImportDialog } from './components/DeploymentCSVImportDialog';
import { DeploymentExecutionDialog } from './components/DeploymentExecutionDialog';
import { DeploymentItemDeleteDialog } from './components/DeploymentItemDeleteDialog';
import {
  DeploymentStatusBadge,
  DeploymentItemStatusBadge,
  CampaignTypeBadge,
} from './components/StatusBadges';
import { useDeployment } from '../../hooks/useDeployment';
import { useExecuteDeployment } from '../../hooks/useExecuteDeployment';
import { useRetryDeploymentItem } from '../../hooks/useRetryDeploymentItem';
import { useDeleteDeploymentItem } from '../../hooks/useDeleteDeploymentItem';
import { useDeploymentExecution } from '../../hooks/useDeploymentExecution';
import { getJiraIssueUrl } from '../../utils/jiraUrls';
import AppContext from '../../contexts/AppContext';
import { DEPLOYMENT_STATUS, DEPLOYMENT_ITEM_STATUS, type DeploymentItem } from '../../api/deploymentsApi';

export const DeploymentDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { jiraBrowseBase } = useContext(AppContext);
  const [importOpen, setImportOpen] = useState(false);
  const [executeJobId, setExecuteJobId] = useState<string | null>(null);
  const [executeTotal, setExecuteTotal] = useState(0);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DeploymentItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { deployment, isLoading, error, refetch } = useDeployment(id);
  const { mutate: execute, isPending: executing } = useExecuteDeployment(id ?? '', {
    onSuccess: ({ jobId }) => {
      setExecuteJobId(jobId);
      setProgressDialogOpen(true);
    },
  });

  const execution = useDeploymentExecution(id, executeJobId, executeTotal, {
    deploymentName: deployment?.name,
    onCompleted: () => {
      refetch();
    },
  });
  const { mutate: deleteItem, isPending: deletingItem } = useDeleteDeploymentItem(id ?? '', {
    onSuccess: () => {
      setItemToDelete(null);
      setDeleteError(null);
    },
    onError: (err) => setDeleteError(err.message),
  });

  const { mutate: retry, isPending: retrying } = useRetryDeploymentItem(id ?? '', {
    onSuccess: ({ jobId }) => {
      setExecuteJobId(jobId);
      setProgressDialogOpen(true);
    },
  });

  const items = deployment?.items ?? [];
  const progress = deployment?.progress ?? null;
  const inProgress = deployment?.status === DEPLOYMENT_STATUS.IN_PROGRESS;

  const handleRetryItem = useCallback(
    (itemId: number) => {
      setExecuteTotal(1);
      retry(itemId);
    },
    [retry]
  );

  const handleRetryFailed = () => {
    setExecuteTotal(progress?.failed ?? 0);
    retry(undefined);
  };

  const columns = useMemo(
    () => [
      { accessorKey: 'sector', header: t('Sector') },
      { accessorKey: 'shop_sign', header: t('Banner') },
      { accessorKey: 'store_name', header: t('Point of sale') },
      { accessorKey: 'brand', header: t('Brand') },
      { accessorKey: 'furniture_name', header: t('Furniture') },
      { accessorKey: 'technician_name', header: t('Technician') },
      { accessorKey: 'technician_email', header: t('Email') },
      {
        accessorKey: 'scheduled_date',
        header: t('Date'),
        cell: ({ row }: any) => {
          const d = row.original;
          return (
            <span className="text-sm">
              {dayjs(d.scheduled_date).format('DD/MM/YYYY')}
              {d.scheduled_time ? ` ${d.scheduled_time}` : ''}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: t('Status'),
        cell: ({ row }: any) => <DeploymentItemStatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'issue_key',
        header: t('Ticket'),
        cell: ({ row }: any) => {
          const key = row.original.issue_key;
          if (!key) return '—';
          return (
            <a
              href={getJiraIssueUrl(key, jiraBrowseBase)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline text-sm font-medium"
            >
              {key}
              <ExternalLink className="w-3 h-3" />
            </a>
          );
        },
      },
      {
        accessorKey: 'error_message',
        header: t('Error'),
        cell: ({ row }: any) =>
          row.original.error_message ? (
            <span className="text-xs text-red-600" title={row.original.error_message}>
              {row.original.error_message.length > 80
                ? row.original.error_message.slice(0, 80) + '…'
                : row.original.error_message}
            </span>
          ) : (
            '—'
          ),
      },
      {
        id: 'actions',
        header: t('Action'),
        enableSorting: false,
        cell: ({ row }: any) => {
          const item = row.original;
          return (
            <div className="flex gap-1">
              {item.status === DEPLOYMENT_ITEM_STATUS.FAILED && (
                <button
                  onClick={() => handleRetryItem(item.id)}
                  disabled={inProgress || retrying}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-40"
                  title={t('Retry the creation')}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
              {!item.issue_key && (
                <button
                  onClick={() => {
                    setDeleteError(null);
                    setItemToDelete(item);
                  }}
                  className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                  title={t('Delete the item')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [handleRetryItem, inProgress, retrying, t, jiraBrowseBase]
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !deployment) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/deployments')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('Back')}
        </Button>
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          <h3 className="font-bold">{t('An error occurred')}</h3>
          <p>{error?.message ?? t('Deployment not found')}</p>
        </div>
      </div>
    );
  }

  const pendingCount = progress?.pending ?? 0;
  const canExecute = deployment.status !== DEPLOYMENT_STATUS.IN_PROGRESS && pendingCount > 0;

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/admin/deployments')}
        className="-ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {t('Back')}
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Truck className="w-8 h-8" />
            {deployment.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <CampaignTypeBadge type={deployment.campaign_type} />
            <DeploymentStatusBadge status={deployment.status} />
            <span>·</span>
            <span>
              {dayjs(deployment.start_date).format('DD/MM/YYYY')}
              {deployment.end_date ? ` → ${dayjs(deployment.end_date).format('DD/MM/YYYY')}` : ''}
            </span>
            <span>·</span>
            <span>SD {deployment.service_desk_id}</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            onClick={() => setImportOpen(true)}
            size="lg"
            variant="outline"
            disabled={deployment.status === DEPLOYMENT_STATUS.IN_PROGRESS}
          >
            <Upload className="w-4 h-4 mr-2" />
            {t('Import CSV')}
          </Button>
          {(progress?.failed ?? 0) > 0 && (
            <Button
              onClick={handleRetryFailed}
              size="lg"
              variant="outline"
              disabled={inProgress || retrying}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {t('Retry the failures ({{count}})', { count: progress.failed })}
            </Button>
          )}
          <Button
            onClick={() => {
              if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission().catch(() => {});
              }
              setExecuteTotal(pendingCount);
              execute();
            }}
            size="lg"
            disabled={!canExecute || executing}
            className="bg-black hover:bg-gray-800"
          >
            <Play className="w-4 h-4 mr-2" />
            {pendingCount > 0
              ? t('Create {{count}} ticket', { count: pendingCount })
              : t('Launch the creation')}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {progress && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('Pending')}</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{progress.pending}</div>
              <p className="text-xs text-muted-foreground mt-1">{t('Items to create')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('Created count')}</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{progress.created}</div>
              <p className="text-xs text-muted-foreground mt-1">{t('Created tickets')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('Failures')}</CardTitle>
              <XCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{progress.failed}</div>
              <p className="text-xs text-muted-foreground mt-1">{t('Creation errors')}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('Total')}</CardTitle>
              <Hash className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{progress.total}</div>
              <p className="text-xs text-muted-foreground mt-1">{t('Items in the deployment')}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {executeJobId && !progressDialogOpen && !execution.completed && (
        <button
          onClick={() => setProgressDialogOpen(true)}
          className="w-full flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-left transition-colors hover:bg-blue-100"
        >
          <Loader2 className="w-5 h-5 animate-spin text-blue-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">
              {t('Creating tickets in progress')}
            </p>
            <p className="text-xs text-blue-700">
              {execution.progress.current} / {execution.progress.total} ·{' '}
              {execution.progress.total > 0
                ? Math.round((execution.progress.current / execution.progress.total) * 100)
                : 0}
              {t('% — click to see details')}
            </p>
          </div>
          <div className="w-32 bg-blue-200 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-blue-600 h-1.5 transition-all"
              style={{
                width: `${
                  execution.progress.total > 0
                    ? Math.round((execution.progress.current / execution.progress.total) * 100)
                    : 0
                }%`,
              }}
            />
          </div>
        </button>
      )}

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('Deployment items')}</CardTitle>
          <CardDescription>
            {t('List of points of sale and furniture to deploy')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              {t('No item. Import a CSV to start.')}
            </p>
          ) : (
            <DataTable
              data={items}
              columns={columns}
              emptyMessage={t('No item')}
              showGlobalSearch={true}
              showPagination={true}
              pageSize={20}
            />
          )}
        </CardContent>
      </Card>

      <DeploymentCSVImportDialog
        deploymentId={deployment.id}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={refetch}
      />

      <DeploymentExecutionDialog
        open={progressDialogOpen}
        onClose={() => setProgressDialogOpen(false)}
        jobId={executeJobId}
        progress={execution.progress}
        logs={execution.logs}
        completed={execution.completed}
        summary={execution.summary}
        socketConnected={execution.socketConnected}
      />

      <DeploymentItemDeleteDialog
        item={itemToDelete}
        open={!!itemToDelete}
        loading={deletingItem}
        error={deleteError}
        onConfirm={() => itemToDelete && deleteItem(itemToDelete.id)}
        onClose={() => {
          setItemToDelete(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
};

export default DeploymentDetail;
